import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea, EstadoVazio } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import {
  alterarPessoa,
  alterarTurno,
  criarTurno,
  diaLongo,
  diaMes,
  hojeBrasilia,
  incluirFeriado,
  incluirPessoa,
  lerHistorico,
  lerPessoas,
  lerQuadro,
  removerFeriado,
  reordenarPessoas,
  somarDias,
} from '../../escala';
import type {
  AlteracaoDaEscala,
  FeriadoDaEscala,
  PessoaDaEscala,
  TurnoDaEscala,
} from '../../escala';
import { FichaDaPessoa } from './FichaDaPessoa';
import type { ParteDaFicha } from './FichaDaPessoa';
import { resumoDaFolgaFixa } from './folga-fixa';
import { estiloDoTurno } from './QuadroDeHorario';

/**
 * CONFIGURAR (Carlos e Nicolas): os horários de cada turno ("deixa sem horário, o Carlos depois
 * coloca"), os feriados (os nacionais já vêm; os de Muriaé entram aqui) e as pessoas do quadro.
 */

type Aviso = { tom: 'ok' | 'erro'; texto: string } | null;

function useAviso() {
  const [aviso, setAviso] = useState<Aviso>(null);
  const caixa = aviso && (
    <div
      className={`qh-aviso qh-aviso-${aviso.tom}`}
      role={aviso.tom === 'erro' ? 'alert' : 'status'}
    >
      {aviso.texto}
      <button type="button" onClick={() => setAviso(null)} aria-label="Fechar aviso">
        <Icone nome="x" tamanho={14} />
      </button>
    </div>
  );
  return { setAviso, caixa };
}

export function AbaConfigurar({ sessao }: { sessao: Sessao }) {
  return (
    <div className="qh-corpo qh-config">
      <Turnos sessao={sessao} />
      <Pessoas sessao={sessao} />
      <Feriados sessao={sessao} />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Turnos
 * ------------------------------------------------------------------ */

function Turnos({ sessao }: { sessao: Sessao }) {
  const [turnos, setTurnos] = useState<TurnoDaEscala[] | null>(null);
  const [erro, setErro] = useState(false);
  const [novo, setNovo] = useState({ codigo: '', rotulo: '', corTexto: '#111111', corFundo: '' });
  const { setAviso, caixa } = useAviso();
  const carregar = useCallback(() => {
    void lerQuadro().then((r) => (r.ok ? setTurnos([...r.dados.turnos]) : setErro(true)));
  }, []);
  useEffect(carregar, [carregar]);

  const salvar = async (t: TurnoDaEscala, m: Partial<TurnoDaEscala>) => {
    const r = await alterarTurno(sessao, t.codigo, m);
    if (r.tipo === 'confirmada') {
      setTurnos((l) => (l ?? []).map((x) => (x.codigo === t.codigo ? r.dados : x)));
      setAviso({ tom: 'ok', texto: `${r.dados.rotulo}: salvo.` });
    } else
      setAviso({
        tom: 'erro',
        texto: r.tipo === 'recusada' ? r.mensagem : 'A internet caiu — confira.',
      });
  };
  const criar = async () => {
    const r = await criarTurno(sessao, {
      codigo: novo.codigo.trim().toUpperCase(),
      rotulo: novo.rotulo.trim(),
      corTexto: novo.corTexto,
      corFundo: novo.corFundo || null,
    });
    if (r.tipo === 'confirmada') {
      setNovo({ codigo: '', rotulo: '', corTexto: '#111111', corFundo: '' });
      setAviso({ tom: 'ok', texto: `Turno ${r.dados.rotulo} criado.` });
      carregar();
    } else
      setAviso({
        tom: 'erro',
        texto: r.tipo === 'recusada' ? r.mensagem : 'A internet caiu — confira.',
      });
  };

  if (erro) return <ErroDeArea oQue="os turnos" aoTentar={carregar} />;
  return (
    <section className="qh-bloco">
      <h2>Turnos e horários</h2>
      <p className="qh-mudo">
        Os horários aparecem na legenda, na impressão e na “Minha escala” de cada um. Em branco =
        não aparece horário.
      </p>
      {caixa}
      {turnos === null ? (
        <div className="qh-carregando">Carregando…</div>
      ) : (
        <div className="qh-rolagem">
          <table className="qh-config-tabela">
            <thead>
              <tr>
                <th>Célula</th>
                <th>Nome</th>
                <th>Entrada</th>
                <th>Almoço</th>
                <th>Saída</th>
                <th>Cores</th>
                <th>Em uso</th>
              </tr>
            </thead>
            <tbody>
              {turnos.map((t) => (
                <LinhaDoTurno key={t.codigo} t={t} aoSalvar={(m) => void salvar(t, m)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <details className="qh-novo-turno">
        <summary>Criar outro turno de trabalho</summary>
        <div className="qh-linha-campos">
          <label className="qh-campo">
            Código (ex.: D9)
            <input
              value={novo.codigo}
              maxLength={4}
              onChange={(e) => setNovo({ ...novo, codigo: e.target.value.toUpperCase() })}
            />
          </label>
          <label className="qh-campo">
            Nome
            <input
              value={novo.rotulo}
              maxLength={40}
              onChange={(e) => setNovo({ ...novo, rotulo: e.target.value })}
            />
          </label>
          <label className="qh-campo">
            Cor do X
            <input
              type="color"
              value={novo.corTexto}
              onChange={(e) => setNovo({ ...novo, corTexto: e.target.value })}
            />
          </label>
          <label className="qh-campo">
            Fundo
            <input
              type="color"
              value={novo.corFundo || '#ffffff'}
              onChange={(e) => setNovo({ ...novo, corFundo: e.target.value })}
            />
          </label>
          <button
            type="button"
            className="qh-btn qh-btn-pri"
            onClick={() => void criar()}
            disabled={!novo.codigo || novo.rotulo.trim().length < 2}
          >
            Criar
          </button>
        </div>
      </details>
    </section>
  );
}

function LinhaDoTurno({
  t,
  aoSalvar,
}: {
  t: TurnoDaEscala;
  aoSalvar: (m: Partial<TurnoDaEscala>) => void;
}) {
  const [rotulo, setRotulo] = useState(t.rotulo);
  const hora = (campo: 'entrada' | 'almocoInicio' | 'almocoFim' | 'saida', rot: string) => (
    <input
      type="time"
      aria-label={`${rot} de ${t.rotulo}`}
      defaultValue={t[campo] ?? ''}
      onBlur={(e) => {
        const v = e.target.value || null;
        if (v !== t[campo]) aoSalvar({ [campo]: v });
      }}
    />
  );
  const temHorario = t.categoria === 'TRABALHO';
  return (
    <tr className={t.ativo ? undefined : 'qh-desligado'}>
      <td>
        <span className="qh-amostra" style={estiloDoTurno(t)}>
          {t.categoria === 'COMPENSADA' ? 'FC' : t.marca || ' '}
        </span>
      </td>
      <td>
        <input
          value={rotulo}
          maxLength={40}
          aria-label={`Nome do turno ${t.codigo}`}
          onChange={(e) => setRotulo(e.target.value)}
          onBlur={() =>
            rotulo.trim() !== t.rotulo &&
            rotulo.trim().length >= 2 &&
            aoSalvar({ rotulo: rotulo.trim() })
          }
        />
      </td>
      <td>{temHorario ? hora('entrada', 'Entrada') : '—'}</td>
      <td className="qh-almoco">
        {temHorario ? (
          <>
            {hora('almocoInicio', 'Início do almoço')}
            <span>às</span>
            {hora('almocoFim', 'Fim do almoço')}
          </>
        ) : (
          '—'
        )}
      </td>
      <td>{temHorario ? hora('saida', 'Saída') : '—'}</td>
      <td className="qh-cores">
        <input
          type="color"
          aria-label={`Cor do texto de ${t.rotulo}`}
          defaultValue={t.corTexto}
          onBlur={(e) => e.target.value !== t.corTexto && aoSalvar({ corTexto: e.target.value })}
        />
        <input
          type="color"
          aria-label={`Cor de fundo de ${t.rotulo}`}
          defaultValue={t.corFundo ?? '#ffffff'}
          onBlur={(e) =>
            e.target.value !== (t.corFundo ?? '#ffffff') && aoSalvar({ corFundo: e.target.value })
          }
        />
      </td>
      <td>
        <input
          type="checkbox"
          checked={t.ativo}
          aria-label={`${t.rotulo} em uso`}
          onChange={(e) => aoSalvar({ ativo: e.target.checked })}
        />
      </td>
    </tr>
  );
}

/* ------------------------------------------------------------------ *
 * Pessoas
 * ------------------------------------------------------------------ */

function Pessoas({ sessao }: { sessao: Sessao }) {
  const [pessoas, setPessoas] = useState<PessoaDaEscala[] | null>(null);
  const [contas, setContas] = useState<{ accountId: string; nome: string; login: string }[]>([]);
  const [erro, setErro] = useState(false);
  const [conta, setConta] = useState('');
  const [nome, setNome] = useState('');
  const [turnos, setTurnos] = useState<TurnoDaEscala[]>([]);
  const [ficha, setFicha] = useState<{ pessoa: PessoaDaEscala; parte: ParteDaFicha } | null>(null);
  const { setAviso, caixa } = useAviso();
  useEffect(() => {
    void lerQuadro().then(
      (r) => r.ok && setTurnos(r.dados.turnos.filter((t) => t.ativo && t.categoria === 'TRABALHO')),
    );
  }, []);
  const carregar = useCallback(() => {
    void lerPessoas().then((r) => {
      if (!r.ok) return setErro(true);
      setPessoas(r.dados.pessoas);
      setContas(r.dados.contasFora);
    });
  }, []);
  useEffect(carregar, [carregar]);

  const resultado = (r: { tipo: string; mensagem?: string }, ok: string) => {
    if (r.tipo === 'confirmada') {
      setAviso({ tom: 'ok', texto: ok });
      carregar();
    } else
      setAviso({
        tom: 'erro',
        texto:
          r.tipo === 'recusada' ? (r.mensagem ?? 'Não foi aceito.') : 'A internet caiu — confira.',
      });
  };
  const incluir = async () => {
    const r = await incluirPessoa(
      sessao,
      conta
        ? { accountId: conta, ...(nome.trim() ? { nome: nome.trim() } : {}) }
        : { nome: nome.trim() },
    );
    setConta('');
    setNome('');
    resultado(r, 'Pessoa colocada no fim do quadro.');
  };
  const mover = async (i: number, d: -1 | 1) => {
    if (!pessoas) return;
    const ativas = pessoas.filter((p) => p.ativo);
    const j = i + d;
    if (j < 0 || j >= ativas.length) return;
    const ids = ativas.map((p) => p.id);
    [ids[i], ids[j]] = [ids[j] ?? '', ids[i] ?? ''];
    setPessoas([
      ...ids.map((id) => ativas.find((p) => p.id === id)).filter((p): p is PessoaDaEscala => !!p),
      ...pessoas.filter((p) => !p.ativo),
    ]);
    const r = await reordenarPessoas(sessao, ids);
    if (r.tipo !== 'confirmada') resultado(r, '');
  };
  const ligarConta = async (p: PessoaDaEscala, accountId: string | null) => {
    if (!accountId && !window.confirm(`Desligar ${p.nome} da conta do InovaPay?`)) return;
    resultado(
      await alterarPessoa(sessao, p.id, { accountId }),
      accountId
        ? `${p.nome} ligada à conta: já vê a própria escala e faz pedidos.`
        : `${p.nome} desligada da conta.`,
    );
  };
  const ligar = async (p: PessoaDaEscala, ativo: boolean) => {
    if (!ativo && !window.confirm(`Tirar ${p.nome} do quadro? (o histórico dela fica guardado)`))
      return;
    resultado(
      await alterarPessoa(sessao, p.id, { ativo }),
      ativo ? `${p.nome} voltou ao quadro.` : `${p.nome} saiu do quadro.`,
    );
  };

  if (erro) return <ErroDeArea oQue="as pessoas do quadro" aoTentar={carregar} />;
  const ativas = (pessoas ?? []).filter((p) => p.ativo);
  const inativas = (pessoas ?? []).filter((p) => !p.ativo);
  return (
    <section className="qh-bloco">
      <h2>Pessoas do quadro</h2>
      <p className="qh-mudo">
        A ordem aqui é a ordem das linhas do quadro. Ligar a pessoa à conta do InovaPay dela deixa
        ela ver a própria escala e fazer pedidos.
      </p>
      {caixa}
      <div className="qh-linha-campos">
        <label className="qh-campo">
          Conta do InovaPay
          <select value={conta} onChange={(e) => setConta(e.target.value)}>
            <option value="">Sem conta (só o nome)</option>
            {contas.map((c) => (
              <option key={c.accountId} value={c.accountId}>
                {c.nome} · cód {c.login}
              </option>
            ))}
          </select>
        </label>
        <label className="qh-campo">
          Nome no quadro {conta ? '(opcional)' : ''}
          <input value={nome} maxLength={40} onChange={(e) => setNome(e.target.value)} />
        </label>
        <button
          type="button"
          className="qh-btn qh-btn-pri"
          onClick={() => void incluir()}
          disabled={!conta && nome.trim().length < 2}
        >
          <Icone nome="plus" tamanho={15} />
          Colocar no quadro
        </button>
      </div>
      {pessoas === null ? (
        <div className="qh-carregando">Carregando…</div>
      ) : ativas.length === 0 ? (
        <EstadoVazio icone="users" titulo="Quadro vazio" texto="Coloque a primeira pessoa acima." />
      ) : (
        <ol className="qh-pessoas">
          {ativas.map((p, i) => (
            <li key={p.id}>
              <span className="qh-pessoa-pos">{i + 1}</span>
              <span className="qh-pessoa-nome">
                {p.nome}
                {p.funcao && <small className="qh-tag-funcao">{p.funcao}</small>}
                {p.accountId ? (
                  <button
                    type="button"
                    className="qh-tag-conta qh-tag-botao"
                    title="Ligada à conta do InovaPay — toque para desligar"
                    onClick={() => void ligarConta(p, null)}
                  >
                    com conta
                  </button>
                ) : (
                  // ligar à conta: a pessoa passa a ver a própria escala e a fazer pedidos
                  <select
                    className="qh-ligar"
                    value=""
                    aria-label={`Ligar ${p.nome} à conta do InovaPay`}
                    onChange={(e) => e.target.value && void ligarConta(p, e.target.value)}
                  >
                    <option value="">só nome · ligar à conta…</option>
                    {contas.map((c) => (
                      <option key={c.accountId} value={c.accountId}>
                        {c.nome} · cód {c.login}
                      </option>
                    ))}
                  </select>
                )}
              </span>
              <button
                type="button"
                className={`qh-folga-fixa${p.folgaFixa ? ' qh-folga-fixa-on' : ''}`}
                onClick={() => setFicha({ pessoa: p, parte: 'folga' })}
                title="Folga fixa e turno padrão"
              >
                <Icone nome="cal" tamanho={13} />
                {p.folgaFixa ? resumoDaFolgaFixa(p.folgaFixa, turnos) : 'Pôr folga fixa'}
              </button>
              <button
                type="button"
                className="qh-btn-icone"
                onClick={() => void mover(i, -1)}
                disabled={i === 0}
                aria-label={`Subir ${p.nome}`}
              >
                <Icone nome="up-t" tamanho={14} />
              </button>
              <button
                type="button"
                className="qh-btn-icone qh-gira"
                onClick={() => void mover(i, 1)}
                disabled={i === ativas.length - 1}
                aria-label={`Descer ${p.nome}`}
              >
                <Icone nome="up-t" tamanho={14} />
              </button>
              <button
                type="button"
                className="qh-btn-icone"
                onClick={() => setFicha({ pessoa: p, parte: 'dados' })}
                aria-label={`Ficha de ${p.nome}`}
              >
                <Icone nome="edit" tamanho={14} />
              </button>
              <button
                type="button"
                className="qh-btn-icone"
                onClick={() => void ligar(p, false)}
                aria-label={`Tirar ${p.nome} do quadro`}
              >
                <Icone nome="x" tamanho={14} />
              </button>
            </li>
          ))}
        </ol>
      )}
      {ficha && (
        <FichaDaPessoa
          sessao={sessao}
          pessoa={ficha.pessoa}
          parte={ficha.parte}
          turnos={turnos}
          contas={contas}
          aoFechar={() => setFicha(null)}
          aoMudar={() => carregar()}
        />
      )}
      {inativas.length > 0 && (
        <details>
          <summary>Fora do quadro ({inativas.length})</summary>
          <ul className="qh-pessoas">
            {inativas.map((p) => (
              <li key={p.id}>
                <span className="qh-pessoa-nome">{p.nome}</span>
                <button
                  type="button"
                  className="qh-btn qh-btn-sec qh-btn-curto"
                  onClick={() => void ligar(p, true)}
                >
                  Voltar ao quadro
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Feriados
 * ------------------------------------------------------------------ */

function Feriados({ sessao }: { sessao: Sessao }) {
  const hoje = hojeBrasilia();
  const [lista, setLista] = useState<FeriadoDaEscala[] | null>(null);
  const [dia, setDia] = useState('');
  const [nome, setNome] = useState('');
  const { setAviso, caixa } = useAviso();
  const carregar = useCallback(() => {
    const de = somarDias(hoje, -60);
    void lerQuadro(de, somarDias(de, 93)).then((r) => r.ok && setLista([...r.dados.feriados]));
    // o quadro limita 93 dias por vez: junta o resto do ano seguinte
    void lerQuadro(somarDias(de, 94), somarDias(de, 187)).then(
      (r) =>
        r.ok &&
        setLista((l) => [
          ...(l ?? []),
          ...r.dados.feriados.filter((f) => !(l ?? []).some((x) => x.dia === f.dia)),
        ]),
    );
    void lerQuadro(somarDias(de, 188), somarDias(de, 281)).then(
      (r) =>
        r.ok &&
        setLista((l) => [
          ...(l ?? []),
          ...r.dados.feriados.filter((f) => !(l ?? []).some((x) => x.dia === f.dia)),
        ]),
    );
    void lerQuadro(somarDias(de, 282), somarDias(de, 375)).then(
      (r) =>
        r.ok &&
        setLista((l) => [
          ...(l ?? []),
          ...r.dados.feriados.filter((f) => !(l ?? []).some((x) => x.dia === f.dia)),
        ]),
    );
  }, [hoje]);
  useEffect(carregar, [carregar]);

  const incluir = async () => {
    const r = await incluirFeriado(sessao, { dia, nome: nome.trim() });
    if (r.tipo === 'confirmada') {
      setDia('');
      setNome('');
      setAviso({ tom: 'ok', texto: `Feriado ${diaMes(r.dados.dia)} salvo.` });
      setLista(null);
      carregar();
    } else
      setAviso({
        tom: 'erro',
        texto: r.tipo === 'recusada' ? r.mensagem : 'A internet caiu — confira.',
      });
  };
  const remover = async (f: FeriadoDaEscala) => {
    if (!window.confirm(`Tirar o feriado de ${diaMes(f.dia)} (${f.nome})?`)) return;
    const r = await removerFeriado(sessao, f.dia);
    if (r.tipo === 'confirmada') setLista((l) => (l ?? []).filter((x) => x.dia !== f.dia));
    else
      setAviso({
        tom: 'erro',
        texto: r.tipo === 'recusada' ? r.mensagem : 'A internet caiu — confira.',
      });
  };

  return (
    <section className="qh-bloco">
      <h2>Feriados</h2>
      <p className="qh-mudo">
        Os nacionais já vêm. Ponha os de Muriaé. Quem trabalha no feriado fica com 1 folga a
        compensar (aparece no Controle).
      </p>
      {caixa}
      <div className="qh-linha-campos">
        <label className="qh-campo">
          Dia
          <input type="date" value={dia} onChange={(e) => setDia(e.target.value)} />
        </label>
        <label className="qh-campo">
          Nome
          <input
            value={nome}
            maxLength={60}
            placeholder="ex.: Aniversário de Muriaé"
            onChange={(e) => setNome(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="qh-btn qh-btn-pri"
          onClick={() => void incluir()}
          disabled={!dia || nome.trim().length < 2}
        >
          <Icone nome="plus" tamanho={15} />
          Pôr feriado
        </button>
      </div>
      {lista === null ? (
        <div className="qh-carregando">Carregando…</div>
      ) : (
        <ul className="qh-feriados">
          {[...lista]
            .sort((a, b) => a.dia.localeCompare(b.dia))
            .map((f) => (
              <li key={f.dia} className={f.dia < hoje ? 'qh-passado' : undefined}>
                <span className="qh-amostra qh-amostra-feriado">{Number(f.dia.slice(8, 10))}</span>
                <span>
                  <strong>{diaLongo(f.dia)}</strong>/{f.dia.slice(0, 4)} · {f.nome}
                </span>
                <button
                  type="button"
                  className="qh-btn-icone"
                  onClick={() => void remover(f)}
                  aria-label={`Tirar feriado ${f.nome}`}
                >
                  <Icone nome="x" tamanho={14} />
                </button>
              </li>
            ))}
        </ul>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Histórico
 * ------------------------------------------------------------------ */

export function AbaHistorico() {
  const [h, setH] = useState<AlteracaoDaEscala[] | null>(null);
  const [erro, setErro] = useState(false);
  const [turnos, setTurnos] = useState<Map<string, TurnoDaEscala>>(new Map());
  const carregar = useCallback(() => {
    setErro(false);
    void lerHistorico().then((r) => (r.ok ? setH(r.dados.alteracoes) : setErro(true)));
    void lerQuadro().then(
      (r) => r.ok && setTurnos(new Map(r.dados.turnos.map((t) => [t.codigo, t]))),
    );
  }, []);
  useEffect(carregar, [carregar]);
  if (erro) return <ErroDeArea oQue="o histórico" aoTentar={carregar} />;
  if (h === null) return <div className="qh-carregando">Carregando…</div>;
  if (h.length === 0)
    return (
      <EstadoVazio
        icone="clock"
        titulo="Nada mudou ainda"
        texto="Cada célula mexida aparece aqui, com quem mexeu e quando."
      />
    );
  const nome = (x: AlteracaoDaEscala['antes']) =>
    x ? (turnos.get(x.turno)?.rotulo ?? x.turno) : 'vazio';
  return (
    <div className="qh-corpo">
      <ul className="qh-historico">
        {h.map((x) => (
          <li key={x.id}>
            <span className="qh-hist-quando">
              {new Date(x.em).toLocaleString('pt-BR', {
                timeZone: 'America/Sao_Paulo',
                dateStyle: 'short',
                timeStyle: 'short',
              })}
            </span>
            <span>
              <strong>{x.por}</strong> mudou <strong>{x.pessoa}</strong> em {diaLongo(x.dia)}:{' '}
              {nome(x.antes)} → <strong>{nome(x.depois)}</strong>
              {x.pedidoId && <small className="qh-tag-conta">pedido aprovado</small>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
